import React from 'react';

import { Table, Icon, SemanticWIDTHS, Pagination, Dropdown, Responsive, Grid, Loader, DropdownProps, PaginationProps, Popup, Button, Placeholder } from "semantic-ui-react";
import CustomLoader from '../CustomLoader';

export interface ITableAppRender {
    header: string;
    content: any;
    width?: SemanticWIDTHS;
    textAlign?: "center" | "left" | "right" | undefined;
}

export interface IPagination {
    currentPage: string | number | undefined;
    totalRecords: number;
    totalPages: string | number;
    onPageChange?: (pageNumber: string | number | undefined) => void;
}

interface ITableList {
    positive?: boolean;
    error?: boolean;
    warning?: boolean;
    [key: string]: any;
}

interface ITableApp {
    render: ITableAppRender[];
    loading: boolean;
    list: ITableList[];
    actions?: (data: any, index: number) => JSX.Element[] | undefined;
    pagination?: IPagination;
    onChangeRecords?: (value: any) => void;
}

const TableApp: React.FC<ITableApp> = ({ render, actions, loading, list, pagination, onChangeRecords }) => {

    const renderHeaderRow = (render: ITableAppRender[]) => {
        return (
            <Table.Row>
                {render.map(x =>
                    <Table.HeaderCell
                        key={`row-headercell-${x.header}`}
                        width={x.width !== undefined ? x.width : undefined}
                        textAlign={x.textAlign !== undefined ? x.textAlign : undefined}
                    >
                        {x.header}
                    </Table.HeaderCell>
                )}
                {actions && <Table.HeaderCell key={`row-headercell-actions`} width={2} />}
            </Table.Row>
        );
    }

    const renderActions = (data: any, index: number) => {
        if (actions) {
            const elements = actions(data, index);

            if (elements) {
                return elements.length > 3
                    ? <Popup
                        trigger={<Button color="grey" icon="th" />}
                        on="click"
                        content={
                            <Grid columns="4">
                                {elements.map((element, index) =>
                                    <Grid.Column key={`element-action-${index}`}>
                                        {element}
                                    </Grid.Column>
                                )}
                            </Grid>
                        }
                    />
                    : actions(data, index);
            }
        }
    }

    const renderBodyRows = (list: ITableList[], render: ITableAppRender[]) => {
        return (
            list.map((x, i) =>
                <Table.Row key={`row-${i}`} positive={x.positive} error={x.error} warning={x.warning}>
                    {render.map((c, ci) =>
                        <Table.Cell key={`row-${i}-cell-${ci}`} textAlign={c.textAlign !== undefined ? c.textAlign : undefined}>
                            {c.content(x)}
                        </Table.Cell>
                    )}
                    {actions &&
                        <Table.Cell key={`row-${i}-cell-actions`} textAlign="right">
                            {renderActions(x, i)}
                        </Table.Cell>
                    }
                </Table.Row>
            )
        );
    }
    const onChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        if (onChangeRecords) {
            onChangeRecords(data.value);
        }
    }

    const onPageChange = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, data: PaginationProps) => {
        if (pagination?.onPageChange) {
            pagination.onPageChange(data.activePage);
        }
    }

    return (
        <>
            <Grid>
                {pagination &&
                    <Grid.Row>
                        <Grid.Column textAlign="right">
                            <label>Total de {pagination.totalRecords} registros, mostrando por página: </label>
                            <Dropdown
                                labeled
                                selection
                                options={[
                                    { key: "20", text: "20", value: 20 },
                                    { key: "50", text: "50", value: 50 },
                                    { key: "100", text: "100", value: 100 },
                                    { key: "500", text: "500", value: 500 },
                                    { key: "2000", text: "2000", value: 2000 },
                                    { key: "5000", text: "5000", value: 5000 },
                                    { key: "10000", text: "10000", value: 10000 }
                                ]}
                                defaultValue={20}
                                onChange={onChange}
                            >
                            </Dropdown>
                        </Grid.Column>
                    </Grid.Row>}
                <Grid.Row>
                    <Grid.Column>
                        <Table color="pink" selectable striped>
                            <Responsive as={Table.Header} {...Responsive.onlyComputer}>
                                {renderHeaderRow(render)}
                            </Responsive>
                            <Table.Body>
                                {loading &&
                                    <Table.Row>
                                        <Table.Cell textAlign="center" colSpan={render.length + 1}>
                                            <CustomLoader />
                                        </Table.Cell>
                                    </Table.Row>
                                }
                                {!loading && list.length === 0 &&
                                    <Table.Row>
                                        <Table.Cell textAlign="center" colSpan={render.length + 1} negative>
                                            Sem registros
                                        </Table.Cell>
                                    </Table.Row>}
                                {!loading && renderBodyRows(list, render)}
                            </Table.Body>
                        </Table>
                        {pagination && pagination.totalPages > 0 &&
                            <Pagination
                                floated="right"
                                defaultActivePage={pagination.currentPage}
                                ellipsisItem={{ content: <Icon name="ellipsis horizontal" />, icon: true }}
                                firstItem={{ content: <Icon name="angle double left" />, icon: true }}
                                lastItem={{ content: <Icon name="angle double right" />, icon: true }}
                                prevItem={{ content: <Icon name="angle left" />, icon: true }}
                                nextItem={{ content: <Icon name="angle right" />, icon: true }}
                                onPageChange={onPageChange}
                                totalPages={pagination.totalPages}
                            />
                        }
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        </>
    );
}

export default TableApp;
